import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MessageCircle, FileText, Upload, X, Edit, HelpCircle, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserSettings = () => {
  const [settings, setSettings] = useState({
    whatsapp_message_template: '',
    invoice_footer_text: '',
    invoice_terms_conditions: '',
    default_invoice_prefix: 'INV',
    invoice_sequence_start: 1,
    enable_whatsapp_notifications: true,
    enable_email_notifications: true,
    organization_logo: null,
    signature_image: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [isTogglingOnboarding, setIsTogglingOnboarding] = useState(false);
  
  useEffect(() => {
    fetchSettings();
    fetchOnboardingStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setOnboardingCompleted(response.data.onboarding_completed || false);
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
    }
  };

  const handleToggleOnboarding = async () => {
    setIsTogglingOnboarding(true);
    try {
      if (onboardingCompleted) {
        // Reset onboarding to show wizard again
        await axios.post(`${API}/auth/reset-onboarding`);
        setOnboardingCompleted(false);
        alert('Onboarding wizard enabled! It will appear on your next dashboard visit. Refresh the page to see it now.');
      } else {
        // Mark onboarding as completed to hide wizard
        await axios.post(`${API}/auth/complete-onboarding`);
        setOnboardingCompleted(true);
        alert('Onboarding wizard disabled. You can re-enable it anytime from settings.');
      }
    } catch (error) {
      console.error('Error toggling onboarding:', error);
      alert('Failed to update onboarding preference. Please try again.');
    } finally {
      setIsTogglingOnboarding(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoUpload = async (e) => {
    console.log('Logo upload handler called', e);
    const file = e.target.files[0];
    console.log('Selected logo file:', file);
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Only JPEG, JPG, PNG, and GIF images are allowed.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size too large. Maximum allowed size is 5MB.');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/settings/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSettings(prev => ({
        ...prev,
        organization_logo: response.data.logo_url
      }));
      alert('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Error uploading logo: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSignatureUpload = async (e) => {
    console.log('Signature upload handler called', e);
    const file = e.target.files[0];
    console.log('Selected signature file:', file);
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Only JPEG, JPG, PNG, and GIF images are allowed.');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('File size too large. Maximum allowed size is 2MB.');
      return;
    }

    setIsUploadingSignature(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/settings/upload-signature`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSettings(prev => ({
        ...prev,
        signature_image: response.data.signature_url
      }));
      alert('Signature uploaded successfully!');
    } catch (error) {
      console.error('Error uploading signature:', error);
      alert('Error uploading signature: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsUploadingSignature(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.delete(`${API}/settings/remove-logo`);
      setSettings(prev => ({
        ...prev,
        organization_logo: null
      }));
      alert('Logo removed successfully!');
    } catch (error) {
      console.error('Error removing logo:', error);
      alert('Error removing logo: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRemoveSignature = async () => {
    try {
      await axios.delete(`${API}/settings/remove-signature`);
      setSettings(prev => ({
        ...prev,
        signature_image: null
      }));
      alert('Signature removed successfully!');
    } catch (error) {
      console.error('Error removing signature:', error);
      alert('Error removing signature: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50">
      <div className="bg-white/80 backdrop-blur-sm border-b border-purple-100/50 px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            Settings
          </h1>
          <p className="text-gray-600 text-lg">Customize your messaging templates and preferences</p>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto space-y-8">
        {/* Onboarding Wizard Toggle */}
        <Card className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center text-orange-900">
              <HelpCircle className="w-6 h-6 mr-2 text-orange-600" />
              Onboarding Wizard
            </CardTitle>
            <CardDescription className="text-orange-800">
              Control the guided tour that appears when you visit the dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border-2 border-orange-100">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1">Show Welcome Wizard</h4>
                <p className="text-sm text-gray-600">
                  {onboardingCompleted 
                    ? "The onboarding wizard is currently disabled. Enable it to see the guided tour again." 
                    : "The onboarding wizard will appear on your next dashboard visit to guide you through the platform."}
                </p>
              </div>
              <div className="ml-4">
                <button
                  onClick={handleToggleOnboarding}
                  disabled={isTogglingOnboarding}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                    onboardingCompleted ? 'bg-gray-300' : 'bg-gradient-to-r from-orange-500 to-red-500'
                  } ${isTogglingOnboarding ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  role="switch"
                  aria-checked={!onboardingCompleted}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                      onboardingCompleted ? 'translate-x-1' : 'translate-x-7'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>💡 Tip:</strong> The wizard provides a quick 6-step tour covering Clients, Services, Tasks, Task Templates, and Dashboard features. Great for new users or as a refresher!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Settings */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageCircle className="w-6 h-6 mr-2 text-green-600" />
              WhatsApp & Telegram Settings
            </CardTitle>
            <CardDescription>
              Customize message templates for client communications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="whatsapp_message_template">Default Message Template</Label>
              <textarea
                id="whatsapp_message_template"
                name="whatsapp_message_template"
                value={settings.whatsapp_message_template}
                onChange={handleInputChange}
                className="w-full h-24 p-3 border border-gray-200 rounded-md resize-none"
                placeholder="Hi {client_name}, your invoice from {organization_name}..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Available variables: {"{client_name}, {organization_name}, {amount}, {due_date}"}
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enable_whatsapp_notifications"
                name="enable_whatsapp_notifications"
                checked={settings.enable_whatsapp_notifications}
                onChange={handleInputChange}
                className="rounded"
              />
              <Label htmlFor="enable_whatsapp_notifications">Enable WhatsApp notifications</Label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enable_email_notifications"
                name="enable_email_notifications"
                checked={settings.enable_email_notifications}
                onChange={handleInputChange}
                className="rounded"
              />
              <Label htmlFor="enable_email_notifications">Enable email notifications</Label>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Settings */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="w-6 h-6 mr-2 text-blue-600" />
              Invoice Settings
            </CardTitle>
            <CardDescription>
              Customize invoice templates and content
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="default_invoice_prefix">Invoice Number Prefix</Label>
                <Input
                  id="default_invoice_prefix"
                  name="default_invoice_prefix"
                  value={settings.default_invoice_prefix}
                  onChange={handleInputChange}
                  placeholder="INV"
                />
                <p className="text-xs text-gray-500 mt-1">Example: INV, BILL, TAX</p>
              </div>
              
              <div>
                <Label htmlFor="invoice_sequence_start">Sequence Start Number</Label>
                <Input
                  id="invoice_sequence_start"
                  name="invoice_sequence_start"
                  type="number"
                  min="1"
                  value={settings.invoice_sequence_start}
                  onChange={handleInputChange}
                  placeholder="1"
                />
                <p className="text-xs text-gray-500 mt-1">First invoice number to start from</p>
              </div>
            </div>

            <div>
              <Label htmlFor="invoice_footer_text">Invoice Footer Text</Label>
              <Input
                id="invoice_footer_text"
                name="invoice_footer_text"
                value={settings.invoice_footer_text}
                onChange={handleInputChange}
                placeholder="Thank you for your business!"
              />
            </div>

            <div>
              <Label htmlFor="invoice_terms_conditions">Terms & Conditions</Label>
              <textarea
                id="invoice_terms_conditions"
                name="invoice_terms_conditions"
                value={settings.invoice_terms_conditions}
                onChange={handleInputChange}
                className="w-full h-24 p-3 border border-gray-200 rounded-md resize-none"
                placeholder="Payment due within 30 days..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Branding Settings */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Upload className="w-6 h-6 mr-2 text-indigo-600" />
              Branding & Signature Settings
            </CardTitle>
            <CardDescription>
              Upload your organization logo and signature for invoices
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Logo Upload Section */}
            <div className="space-y-4">
              <Label>Organization Logo (Optional)</Label>
              <div className="flex items-center space-x-4">
                {settings.organization_logo ? (
                  <div className="relative">
                    <img 
                      src={settings.organization_logo} 
                      alt="Organization Logo" 
                      className="h-20 w-20 object-contain border border-gray-200 rounded-md bg-gray-50"
                    />
                    <Button
                      onClick={handleRemoveLogo}
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="h-20 w-20 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                    <Upload className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    id="logo-upload-input"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      document.getElementById('logo-upload-input').click();
                    }}
                    disabled={isUploadingLogo}
                    variant="outline"
                    className="w-full"
                  >
                    {isUploadingLogo ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {settings.organization_logo ? 'Replace Logo' : 'Upload Logo'}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">
                    Max 5MB. Supports JPEG, JPG, PNG, GIF
                  </p>
                </div>
              </div>
            </div>

            {/* Signature Upload Section */}
            <div className="space-y-4">
              <Label>Digital Signature (Optional)</Label>
              <div className="flex items-center space-x-4">
                {settings.signature_image ? (
                  <div className="relative">
                    <img 
                      src={settings.signature_image} 
                      alt="Digital Signature" 
                      className="h-16 w-32 object-contain border border-gray-200 rounded-md bg-gray-50"
                    />
                    <Button
                      onClick={handleRemoveSignature}
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="h-16 w-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                    <Edit className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    id="signature-upload-input"
                    accept="image/*"
                    onChange={handleSignatureUpload}
                    style={{ display: 'none' }}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      document.getElementById('signature-upload-input').click();
                    }}
                    disabled={isUploadingSignature}
                    variant="outline"
                    className="w-full"
                  >
                    {isUploadingSignature ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Edit className="w-4 h-4 mr-2" />
                        {settings.signature_image ? 'Replace Signature' : 'Upload Signature'}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">
                    Max 2MB. Will be used in invoice signature field. Supports JPEG, JPG, PNG, GIF
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main App Component

export default UserSettings;
